import { Fragment, useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

function Sidebar({ active }: { active: string }) {
  const { t } = useTranslation();
  const MENU = [
    { labelKey: "account.settings", path: "/account/settings", icon: "⚙" },
    { labelKey: "account.reports", path: "/account/reports", icon: "📄" },
    { labelKey: "account.accessibility", path: "/account/accessibility", icon: "♿" },
    { labelKey: "account.plans", path: "/account/plans", icon: "💳" },
  ];
  return (
    <div style={{ width: "220px", flexShrink: 0, background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "16px 0", height: "fit-content" }}>
      {MENU.map(item => (
        <Link key={item.path} to={item.path}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 20px", fontSize: "14px", color: active === item.path ? "#C9A84C" : "var(--text-primary)", fontWeight: active === item.path ? 700 : 400, background: active === item.path ? "rgba(201,168,76,0.08)" : "transparent", borderLeft: active === item.path ? "3px solid #C9A84C" : "3px solid transparent", textDecoration: "none" }}>
          <span>{item.icon}</span> {t(item.labelKey)}
        </Link>
      ))}
      <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
      <button onClick={() => { localStorage.removeItem("token"); window.location.href = "/"; }}
        style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 20px", fontSize: "14px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500, borderLeft: "3px solid transparent" }}>
        <span>🚪</span> {t("account.logout")}
      </button>
    </div>
  );
}

type CgReport = {
  id: string; report_date: string; client_name: string;
  assessment_year: string; financial_year: string; description: string;
  has_excel: boolean; has_pdf: boolean; has_csv: boolean;
  excel_filename: string; file_size: number;
};

type BsReport = {
  id: string; converted_date: string; client_name: string;
  bank_name: string; period: string; has_excel: boolean;
  excel_filename: string; file_size: number;
};

type SortState = { col: string; dir: "asc" | "desc" };

function sortRows<T extends Record<string, any>>(data: T[], sort: SortState): T[] {
  return [...data].sort((a, b) => {
    const av = String(a[sort.col] ?? "");
    const bv = String(b[sort.col] ?? "");
    return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });
}

function applyColFilters<T extends Record<string, any>>(data: T[], filters: Record<string, string>): T[] {
  return data.filter(row =>
    Object.entries(filters).every(([col, val]) =>
      !val || String(row[col] ?? "").toLowerCase().includes(val.toLowerCase())
    )
  );
}

function getUniqueVals(data: Record<string, any>[], col: string): string[] {
  return Array.from(new Set(data.map(r => String(r[col] ?? "")))).filter(Boolean).sort();
}

const thStyle: React.CSSProperties = {
  padding: "10px 12px", fontSize: "12px", fontWeight: 700, color: "#5C5C5C",
  background: "#F5F2EE", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1,
  borderBottom: "1.5px solid #EAE6E0", userSelect: "none",
};
const tdStyle: React.CSSProperties = {
  padding: "10px 12px", fontSize: "13px", color: "var(--text-primary)",
  borderBottom: "0.5px solid #EAE6E0", verticalAlign: "middle",
};

function SortBtn({ label, col, sort, onSort }: { label: string; col: string; sort: SortState; onSort: (c: string) => void }) {
  const arrow = sort.col === col ? (sort.dir === "asc" ? " ↑" : " ↓") : " ↕";
  return (
    <button onClick={() => onSort(col)}
      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "12px", fontWeight: 700, color: "#5C5C5C", display: "flex", alignItems: "center", gap: "2px" }}>
      {label}
      <span style={{ fontSize: "10px", color: sort.col === col ? "#C9A84C" : "#AAA" }}>{arrow}</span>
    </button>
  );
}

function ColFilter({ id, col, data, filters, setFilters, open, setOpen }: {
  id: string; col: string; data: Record<string, any>[];
  filters: Record<string, string>; setFilters: (f: Record<string, string>) => void;
  open: string | null; setOpen: (s: string | null) => void;
}) {
  const isActive = !!filters[col];
  const vals = getUniqueVals(data, col);
  return (
    <div style={{ position: "relative", display: "inline-block" }} data-col-filter-area="true">
      <button data-col-filter-area="true"
        onClick={() => setOpen(open === id ? null : id)}
        title="Filter column"
        style={{ background: isActive ? "#C9A84C" : "none", border: "none", cursor: "pointer", padding: "1px 3px", borderRadius: "3px", fontSize: "9px", color: isActive ? "white" : "#AAA", lineHeight: 1 }}>
        ▽
      </button>
      {open === id && (
        <div data-col-filter-area="true"
          style={{ position: "absolute", top: "100%", left: 0, background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: "8px", padding: "4px 0", zIndex: 200, minWidth: "150px", boxShadow: "0 4px 16px rgba(0,0,0,0.14)", maxHeight: "200px", overflowY: "auto" }}>
          <button data-col-filter-area="true"
            onClick={() => { const f = { ...filters }; delete f[col]; setFilters(f); setOpen(null); }}
            style={{ display: "block", width: "100%", padding: "6px 12px", textAlign: "left", background: !isActive ? "rgba(201,168,76,0.1)" : "none", border: "none", cursor: "pointer", fontSize: "12px", color: "var(--text-primary)", fontWeight: !isActive ? 600 : 400 }}>
            All
          </button>
          {vals.map(val => (
            <button key={val} data-col-filter-area="true"
              onClick={() => { setFilters({ ...filters, [col]: val }); setOpen(null); }}
              style={{ display: "block", width: "100%", padding: "6px 12px", textAlign: "left", background: filters[col] === val ? "rgba(201,168,76,0.1)" : "none", border: "none", cursor: "pointer", fontSize: "12px", color: "var(--text-primary)", fontWeight: filters[col] === val ? 600 : 400 }}>
              {val}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThCell({ label, col, sort, onSort, filterId, filterCol, filterData, filters, setFilters, openFilter, setOpenFilter, center }: {
  label: string; col?: string; sort?: SortState; onSort?: (c: string) => void;
  filterId?: string; filterCol?: string; filterData?: Record<string, any>[];
  filters?: Record<string, string>; setFilters?: (f: Record<string, string>) => void;
  openFilter?: string | null; setOpenFilter?: (s: string | null) => void;
  center?: boolean;
}) {
  return (
    <th style={{ ...thStyle, textAlign: center ? "center" : "left" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: center ? "center" : "flex-start" }}>
        {col && sort && onSort ? (
          <SortBtn label={label} col={col} sort={sort} onSort={onSort} />
        ) : (
          <span>{label}</span>
        )}
        {col && filterId && filterCol && filterData && filters && setFilters && openFilter !== undefined && setOpenFilter && (
          <ColFilter id={filterId} col={filterCol} data={filterData} filters={filters} setFilters={setFilters} open={openFilter} setOpen={setOpenFilter} />
        )}
      </div>
    </th>
  );
}

export default function Reports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [cgReports, setCgReports] = useState<CgReport[]>([]);
  const [bsReports, setBsReports] = useState<BsReport[]>([]);
  const [activeTab, setActiveTab] = useState<"capital-gain" | "bank-statement">("capital-gain");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [cgSort, setCgSort] = useState<SortState>({ col: "report_date", dir: "desc" });
  const [bsSort, setBsSort] = useState<SortState>({ col: "converted_date", dir: "desc" });
  const [cgColFilters, setCgColFilters] = useState<Record<string, string>>({});
  const [bsColFilters, setBsColFilters] = useState<Record<string, string>>({});
  const [openColFilter, setOpenColFilter] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest && !target.closest("[data-col-filter-area]")) {
        setOpenColFilter(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchReports = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    setLoading(true);
    try {
      const [cgRes, bsRes] = await Promise.all([
        fetch("/api/reports/capital-gain", { headers: { Authorization: "Bearer " + token } }),
        fetch("/api/reports/bank-statement", { headers: { Authorization: "Bearer " + token } }),
      ]);
      const [cgData, bsData] = await Promise.all([cgRes.json(), bsRes.json()]);
      setCgReports(Array.isArray(cgData) ? cgData : []);
      setBsReports(Array.isArray(bsData) ? bsData : []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const cgSearched = cgReports.filter(r =>
    !search ||
    r.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.assessment_year?.toLowerCase().includes(search.toLowerCase()) ||
    r.financial_year?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase()) ||
    r.report_date?.toLowerCase().includes(search.toLowerCase())
  );

  const bsSearched = bsReports.filter(r =>
    !search ||
    r.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.bank_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.period?.toLowerCase().includes(search.toLowerCase()) ||
    r.converted_date?.toLowerCase().includes(search.toLowerCase())
  );

  const cgDisplay = sortRows(applyColFilters(cgSearched, cgColFilters), cgSort);
  const bsDisplay = sortRows(applyColFilters(bsSearched, bsColFilters), bsSort);

  const handleDownload = async (id: string, fileType: string, reportType: string) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`/api/reports/download/${reportType}/${id}?file_type=${fileType}`, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!res.ok) { alert("Download failed"); return; }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileType === "excel" ? "report.xlsx" : `report.${fileType}`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (id: string, reportType: "capital-gain" | "bank-statement") => {
    setDeletingId(id);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`/api/reports/${reportType}/${id}`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        setConfirmDeleteId(null);
        await fetchReports();
      } else {
        alert("Delete failed");
      }
    } catch { alert("Delete failed"); }
    setDeletingId(null);
  };

  const handleDeleteAll = async () => {
    setDeleteAllLoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/reports/delete-all", {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        setShowDeleteAll(false);
        setConfirmText("");
        await fetchReports();
      } else {
        alert("Delete failed");
      }
    } catch { alert("Delete failed"); }
    setDeleteAllLoading(false);
  };

  const toggleCgSort = (col: string) => setCgSort(s => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
  const toggleBsSort = (col: string) => setBsSort(s => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  const dlBtn = (label: string, bg: string, color: string, onClick: () => void) => (
    <button onClick={onClick} style={{ background: bg, color, border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
      ↓ {label}
    </button>
  );

  const trashBtn = (id: string) => (
    <button onClick={() => setConfirmDeleteId(confirmDeleteId === id ? null : id)}
      title="Delete report"
      style={{ background: "none", border: "none", cursor: "pointer", color: "#C0392B", fontSize: "16px", padding: "2px 6px", borderRadius: "4px" }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#FCE4D6"; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "none"; }}>
      🗑
    </button>
  );

  const inlineConfirm = (id: string, reportType: "capital-gain" | "bank-statement", colSpan: number) => (
    <tr key={`confirm-${id}`} style={{ background: "#FFF8F6" }}>
      <td colSpan={colSpan} style={{ padding: "12px 16px", borderBottom: "0.5px solid #FCE4D6" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", fontSize: "13px" }}>
          <span style={{ color: "#843C0C", fontWeight: 600 }}>Delete this report? This cannot be undone.</span>
          <button onClick={() => handleDelete(id, reportType)} disabled={deletingId === id}
            style={{ background: "#C0392B", color: "white", border: "none", borderRadius: "6px", padding: "5px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer", opacity: deletingId === id ? 0.7 : 1 }}>
            {deletingId === id ? "Deleting..." : "Yes, Delete"}
          </button>
          <button onClick={() => setConfirmDeleteId(null)}
            style={{ background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: "6px", padding: "5px 14px", fontSize: "12px", cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );

  const emptyState = (link: string, linkLabel: string) => (
    <div style={{ padding: "60px 24px", textAlign: "center" }}>
      <div style={{ fontSize: "48px", marginBottom: "16px" }}>📂</div>
      <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "8px" }}>No reports yet</div>
      <div style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "24px" }}>Reports you generate will appear here</div>
      <Link to={link} style={{ background: "#C9A84C", color: "#2C2C2C", padding: "10px 22px", borderRadius: "8px", fontWeight: 700, fontSize: "14px", textDecoration: "none" }}>
        {linkLabel}
      </Link>
    </div>
  );

  const noMatchState = (clearFn: () => void) => (
    <div style={{ padding: "40px 24px", textAlign: "center" }}>
      <div style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "12px" }}>No reports match your current filters.</div>
      <button onClick={clearFn} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "6px", padding: "6px 14px", fontSize: "13px", cursor: "pointer", color: "var(--text-primary)" }}>
        Clear filters
      </button>
    </div>
  );

  const canDelete = confirmText === "permanently delete";

  if (loading || authLoading) return (
    <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "300px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "36px", height: "36px", border: "3px solid var(--border)", borderTop: "3px solid #C9A84C", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>Loading...</p>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <a href="/account/settings">Account</a>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>My Reports</span>
          </div>
          <h1>My Reports</h1>
          <p className="ph-subtitle">All your generated reports in one place</p>
        </div>
      </div>
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
        <Sidebar active={location.pathname} />
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Header + Search */}
          <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "28px 32px", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "4px", color: "var(--text-primary)" }}>My Reports</h2>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "20px" }}>All reports you have generated are saved here</p>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, description, assessment year, bank name..."
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--border)", borderRadius: "8px", fontSize: "14px", color: "var(--text-primary)", background: "var(--white)", outline: "none", boxSizing: "border-box" }}
              onFocus={e => { e.target.style.borderColor = "#C9A84C"; }}
              onBlur={e => { e.target.style.borderColor = ""; }}
            />
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "2px solid var(--border)", marginBottom: "16px" }}>
            {(["capital-gain", "bank-statement"] as const).map(tab => {
              const label = tab === "capital-gain"
                ? `Capital Gain Reports (${cgReports.length})`
                : `Bank Statement Reports (${bsReports.length})`;
              return (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  style={{ padding: "10px 20px", fontSize: "14px", fontWeight: activeTab === tab ? 700 : 400, color: activeTab === tab ? "#C9A84C" : "var(--text-muted)", background: "transparent", border: "none", borderBottom: activeTab === tab ? "2.5px solid #C9A84C" : "2.5px solid transparent", cursor: "pointer", marginBottom: "-2px", transition: "all 0.15s" }}>
                  {label}
                </button>
              );
            })}
          </div>

          {/* Capital Gain Table */}
          {activeTab === "capital-gain" && (
            <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", overflow: "hidden" }}>
              {cgReports.length === 0
                ? emptyState("/tools/capital-gain", "Generate your first report")
                : cgDisplay.length === 0
                  ? noMatchState(() => { setSearch(""); setCgColFilters({}); })
                  : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "860px" }}>
                        <thead>
                          <tr>
                            <ThCell label="Sl No." center />
                            <ThCell label="Report Date" col="report_date" sort={cgSort} onSort={toggleCgSort}
                              filterId="cg-report_date" filterCol="report_date" filterData={cgSearched}
                              filters={cgColFilters} setFilters={setCgColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Client Name" col="client_name" sort={cgSort} onSort={toggleCgSort}
                              filterId="cg-client_name" filterCol="client_name" filterData={cgSearched}
                              filters={cgColFilters} setFilters={setCgColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="AY / FY" col="assessment_year" sort={cgSort} onSort={toggleCgSort}
                              filterId="cg-assessment_year" filterCol="assessment_year" filterData={cgSearched}
                              filters={cgColFilters} setFilters={setCgColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Description" col="description" sort={cgSort} onSort={toggleCgSort}
                              filterId="cg-description" filterCol="description" filterData={cgSearched}
                              filters={cgColFilters} setFilters={setCgColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Excel" center />
                            <ThCell label="PDF" center />
                            <ThCell label="CSV" center />
                            <ThCell label="" center />
                          </tr>
                        </thead>
                        <tbody>
                          {cgDisplay.map((report, idx) => (
                            <Fragment key={report.id}>
                              <tr
                                style={{ background: idx % 2 === 0 ? "var(--white)" : "var(--offwhite)" }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(201,168,76,0.04)"; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = idx % 2 === 0 ? "var(--white)" : "var(--offwhite)"; }}>
                                <td style={{ ...tdStyle, textAlign: "center", width: "60px" }}>{idx + 1}</td>
                                <td style={{ ...tdStyle, fontSize: "12px", width: "140px" }}>{report.report_date}</td>
                                <td style={{ ...tdStyle, fontWeight: 500, width: "160px" }}>{report.client_name}</td>
                                <td style={{ ...tdStyle, width: "110px" }}>
                                  <div style={{ fontSize: "12px" }}>{report.assessment_year}</div>
                                  {report.financial_year && <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{report.financial_year}</div>}
                                </td>
                                <td style={{ ...tdStyle, width: "200px" }}>
                                  <div title={report.description} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px", fontSize: "12px", color: "var(--text-muted)" }}>
                                    {report.description}
                                  </div>
                                </td>
                                <td style={{ ...tdStyle, textAlign: "center", width: "80px" }}>
                                  {report.has_excel ? dlBtn("Excel", "#E2F0D9", "#375623", () => handleDownload(report.id, "excel", "capital-gain")) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "center", width: "80px" }}>
                                  {report.has_pdf ? dlBtn("PDF", "#FCE4D6", "#843C0C", () => handleDownload(report.id, "pdf", "capital-gain")) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "center", width: "80px" }}>
                                  {report.has_csv ? dlBtn("CSV", "#DDEBF7", "#2F5597", () => handleDownload(report.id, "csv", "capital-gain")) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "center", width: "60px" }}>
                                  {trashBtn(report.id)}
                                </td>
                              </tr>
                              {confirmDeleteId === report.id && inlineConfirm(report.id, "capital-gain", 9)}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
            </div>
          )}

          {/* Bank Statement Table */}
          {activeTab === "bank-statement" && (
            <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", overflow: "hidden" }}>
              {bsReports.length === 0
                ? emptyState("/tools/bank-statement", "Generate your first report")
                : bsDisplay.length === 0
                  ? noMatchState(() => { setSearch(""); setBsColFilters({}); })
                  : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
                        <thead>
                          <tr>
                            <ThCell label="Sl No." center />
                            <ThCell label="Converted Date" col="converted_date" sort={bsSort} onSort={toggleBsSort}
                              filterId="bs-converted_date" filterCol="converted_date" filterData={bsSearched}
                              filters={bsColFilters} setFilters={setBsColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Client Name" col="client_name" sort={bsSort} onSort={toggleBsSort}
                              filterId="bs-client_name" filterCol="client_name" filterData={bsSearched}
                              filters={bsColFilters} setFilters={setBsColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Bank Name" col="bank_name" sort={bsSort} onSort={toggleBsSort}
                              filterId="bs-bank_name" filterCol="bank_name" filterData={bsSearched}
                              filters={bsColFilters} setFilters={setBsColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Statement Period" col="period" sort={bsSort} onSort={toggleBsSort}
                              filterId="bs-period" filterCol="period" filterData={bsSearched}
                              filters={bsColFilters} setFilters={setBsColFilters}
                              openFilter={openColFilter} setOpenFilter={setOpenColFilter} />
                            <ThCell label="Excel" center />
                            <ThCell label="" center />
                          </tr>
                        </thead>
                        <tbody>
                          {bsDisplay.map((report, idx) => (
                            <Fragment key={report.id}>
                              <tr
                                style={{ background: idx % 2 === 0 ? "var(--white)" : "var(--offwhite)" }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(201,168,76,0.04)"; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = idx % 2 === 0 ? "var(--white)" : "var(--offwhite)"; }}>
                                <td style={{ ...tdStyle, textAlign: "center", width: "60px" }}>{idx + 1}</td>
                                <td style={{ ...tdStyle, fontSize: "12px", width: "150px" }}>{report.converted_date}</td>
                                <td style={{ ...tdStyle, fontWeight: 500 }}>{report.client_name}</td>
                                <td style={{ ...tdStyle }}>{report.bank_name}</td>
                                <td style={{ ...tdStyle, fontSize: "12px", color: "var(--text-muted)" }}>{report.period}</td>
                                <td style={{ ...tdStyle, textAlign: "center", width: "90px" }}>
                                  {report.has_excel ? dlBtn("Excel", "#E2F0D9", "#375623", () => handleDownload(report.id, "excel", "bank-statement")) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "center", width: "60px" }}>
                                  {trashBtn(report.id)}
                                </td>
                              </tr>
                              {confirmDeleteId === report.id && inlineConfirm(report.id, "bank-statement", 7)}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
            </div>
          )}

          {/* Danger Zone */}
          <div style={{ marginTop: "48px", padding: "24px", border: "1.5px solid #FCE4D6", borderRadius: "14px", background: "#FFF8F6" }}>
            <h3 style={{ color: "#9C0006", marginBottom: "8px", fontSize: "16px", fontWeight: 700 }}>Danger Zone</h3>
            <p style={{ fontSize: "14px", color: "#5C5C5C", marginBottom: "16px", lineHeight: "1.6" }}>
              Permanently delete all your reports from TaxThinker servers. This action cannot be undone and all downloaded links will stop working.
            </p>
            <button onClick={() => setShowDeleteAll(true)}
              style={{ background: "transparent", border: "1.5px solid #9C0006", color: "#9C0006", padding: "8px 20px", borderRadius: "8px", fontWeight: 600, cursor: "pointer", fontSize: "14px" }}>
              Delete All My Data
            </button>
          </div>

        </div>
      </div>

      {/* Delete All Modal */}
      {showDeleteAll && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
          onClick={e => { if (e.target === e.currentTarget) { setShowDeleteAll(false); setConfirmText(""); } }}>
          <div style={{ background: "var(--white)", borderRadius: "16px", padding: "32px", maxWidth: "480px", width: "100%", boxShadow: "0 24px 60px rgba(0,0,0,0.3)" }}>
            <h2 style={{ color: "#9C0006", fontSize: "18px", fontWeight: 700, marginBottom: "16px" }}>
              Permanently Delete All Reports
            </h2>
            <p style={{ fontSize: "14px", color: "var(--text-primary)", lineHeight: "1.6", marginBottom: "16px" }}>
              This will permanently delete all your reports from TaxThinker servers. This action <strong>CANNOT</strong> be undone. All files will be deleted immediately.
            </p>
            <div style={{ background: "#FFF0F0", border: "1px solid #FCB9B9", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px" }}>
              <p style={{ color: "#9C0006", fontSize: "13px", fontWeight: 600, margin: 0 }}>
                ⚠ All {cgReports.length} Capital Gain report{cgReports.length !== 1 ? "s" : ""} and {bsReports.length} Bank Statement report{bsReports.length !== 1 ? "s" : ""} will be deleted forever.
              </p>
            </div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "8px" }}>
              To confirm, type <span style={{ fontFamily: "monospace", background: "#F5F2EE", padding: "1px 6px", borderRadius: "4px" }}>permanently delete</span> below:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="permanently delete"
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--border)", borderRadius: "8px", fontSize: "14px", outline: "none", marginBottom: "20px", boxSizing: "border-box", color: "var(--text-primary)", background: "var(--white)" }}
              onFocus={e => { e.target.style.borderColor = "#C0392B"; }}
              onBlur={e => { e.target.style.borderColor = ""; }}
            />
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={handleDeleteAll} disabled={!canDelete || deleteAllLoading}
                style={{ flex: 1, background: canDelete ? "#9C0006" : "#CCC", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontWeight: 700, fontSize: "14px", cursor: canDelete ? "pointer" : "not-allowed", transition: "background 0.2s" }}>
                {deleteAllLoading ? "Deleting..." : "Delete Everything"}
              </button>
              <button onClick={() => { setShowDeleteAll(false); setConfirmText(""); }}
                style={{ flex: 1, background: "transparent", color: "var(--text-primary)", border: "1.5px solid var(--border)", borderRadius: "8px", padding: "10px", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
